import React, { useEffect, useState } from "react";
import { useSpeechRecognition } from "../tools/useSpeechRecognition";

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MicIcon from '@mui/icons-material/Mic';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Switch from '@mui/material/Switch';
import WifiIcon from '@mui/icons-material/Wifi';
import BluetoothIcon from '@mui/icons-material/Bluetooth';

import CircularProgress, {
    CircularProgressProps,
} from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useLanguage } from '../../contexts/LanguageContext';
import { LANG_CODE } from "../views/AppFooter";


function CircularProgressWithLabel(
    props: CircularProgressProps & { value: number },
) {
    return (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" {...props} />
            <Box
                sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    variant="caption"
                    component="div"
                    sx={{ color: 'text.secondary' }}
                >{`${Math.round(props.value)}%`}</Typography>
            </Box>
        </Box>
    );
}


// Extend the window object to include SpeechRecognition
declare global {
    interface SpeechRecognition {
        lang: string;
        interimResults: boolean;
        maxAlternatives: number;
        start(): void;
        stop(): void;
        abort(): void;
        onresult: ((event: SpeechRecognitionEvent) => void) | null;
        onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
    }

    interface SpeechRecognitionEvent extends Event {
        results: SpeechRecognitionResultList;
    }

    interface SpeechRecognitionResultList {
        readonly length: number;
        item(index: number): SpeechRecognitionResult;
        [index: number]: SpeechRecognitionResult;
    }

    interface SpeechRecognitionResult {
        readonly length: number;
        readonly isFinal: boolean;
        item(index: number): SpeechRecognitionAlternative;
        [index: number]: SpeechRecognitionAlternative;
    }

    interface SpeechRecognitionAlternative {
        readonly transcript: string;
        readonly confidence: number;
    }

    interface SpeechRecognitionErrorEvent extends Event {
        error: string;
    }

    interface Window {
        SpeechRecognition: {
            new(): SpeechRecognition;
        };
        webkitSpeechRecognition: {
            new(): SpeechRecognition;
        };
    }
}

interface SpeechEvaluatorProps {
    firstWord: string;
    secondWord: string;
}


const SpeechEvaluator: React.FC<SpeechEvaluatorProps> = ({ firstWord, secondWord }) => {
    const { isListening, transcript, error, startListening, stopListening } = useSpeechRecognition();
    const [evaluation, setEvaluation] = useState<string | null>(null);
    const [evalScore, setEvalScore] = useState<number>(0);
    const [saidRandomWord, setSaidRandomWord] = useState<string | null>(null);
    const { language } = useLanguage();

    useEffect(() => {
        if (transcript) {
            evaluateSpeech();
        }
    }, [transcript]);

    const evaluateSpeech = () => {
        if (transcript.trim()) {
            const firstWordSimilarity = similarity(transcript, firstWord);
            const secondWordSimilarity = similarity(transcript, secondWord);

            const similarityThreshold = 0.1;
            if (firstWordSimilarity < similarityThreshold && secondWordSimilarity < similarityThreshold) {
                setEvaluation(`Ops - you said ${transcript}?`);
                setSaidRandomWord(`${transcript}`)
            } else if (firstWordSimilarity > secondWordSimilarity) {
                setEvaluation(`${firstWord} : ${firstWordSimilarity.toFixed(2)} score`);
                setEvalScore(parseFloat(firstWordSimilarity.toFixed(2)));
                setSaidRandomWord(null)
            } else {
                setEvaluation(`${secondWord} : ${secondWordSimilarity.toFixed(2)} score`);
                setEvalScore(parseFloat(secondWordSimilarity.toFixed(2)));
                setSaidRandomWord(null)
            }
        }
    };

    const getText = () => {
        switch (language) {
            case LANG_CODE.EN_PROFESSIONAL:
                return 'Practice your pronunciation';
            case LANG_CODE.JA_PROFESSIONAL:
                return '発音の練習をしてみよう';
            case LANG_CODE.JA_YOUTH:
                return '発音の練習をしてみよう';
            case LANG_CODE.JA_KID:
                return 'はつおんをしてみよう';
        }
    };

    return (
        <div>
            <List
                sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}
                subheader={<ListSubheader> {getText()} </ListSubheader>}
            >
                <ListItem>
                    {transcript ? (
                        <>
                            <ListItemIcon>
                                <IconButton onClick={startListening} disabled={isListening}>
                                    <MicIcon sx={{ fontSize: 35 }} />
                                </IconButton>
                            </ListItemIcon>
                            <p>判定は{transcript}でした。</p>
                            {error && <p>Error: {error}</p>}
                            <ListItemText id="switch-list-label-wifi" primary="" />

                            {!saidRandomWord && (
                                <>
                                    <p>点数 is...</p>
                                    <CircularProgressWithLabel value={evalScore * 100} />
                                </>
                            )}

                        </>
                    ) : (
                        <ListItemIcon sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <IconButton onClick={startListening} disabled={isListening}>
                                <MicIcon sx={{ fontSize: 35 }} />
                            </IconButton>
                        </ListItemIcon>
                    )}
                </ListItem>

            </List>
        </div>
    );
};

export default SpeechEvaluator;

// Utility function to calculate similarity
const similarity = (input: string, target: string): number => {
    const normalizedInput = input.trim().toLowerCase();
    const normalizedTarget = target.trim().toLowerCase();
    let matches = 0;

    for (let i = 0; i < Math.min(normalizedInput.length, normalizedTarget.length); i++) {
        if (normalizedInput[i] === normalizedTarget[i]) {
            matches++;
        }
    }

    return matches / Math.max(normalizedInput.length, normalizedTarget.length);
};
